type BootstrapResponse = {
    ok?: boolean
    status?: number
    json: () => Promise<unknown>
}

type BootstrapFetch = (url: string, init?: RequestInit) => Promise<BootstrapResponse>

type BootstrapRequestOptions = {
    headers?: HeadersInit
    timeoutMs?: number
    fetchImpl?: BootstrapFetch
}

export async function fetchBootstrapJson(
    url: string,
    {
        headers = {},
        timeoutMs = 0,
        fetchImpl = globalThis.fetch.bind(globalThis),
    }: BootstrapRequestOptions = {},
): Promise<unknown> {
    const controller = timeoutMs > 0 ? new AbortController() : null
    const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

    try {
        const response = await fetchImpl(url, {
            method: 'GET',
            redirect: 'follow',
            headers,
            ...(controller ? { signal: controller.signal } : {}),
        })
        if (response.ok === false) throw new Error(`Bootstrap returned ${response.status}`)
        return await response.json()
    } finally {
        if (timeout !== null) clearTimeout(timeout)
    }
}

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 5_000]

// The app can't start without the bootstrap, so a failure is retried until one
// loads, and at once when the browser comes back online. /gon answers a bad or
// missing token with a 200 and no user, so a failure here is only ever the
// network or the server, never a sign the visitor needs to log in.
export async function retryUntilLoaded<T>(
    load: () => Promise<T | null>,
    { onlineTarget = window as Pick<EventTarget, 'addEventListener' | 'removeEventListener'> } = {},
): Promise<T> {
    for (let attempt = 0; ; attempt += 1) {
        const loaded = await load()
        if (loaded) return loaded

        const delayMs = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
        await new Promise<void>(resolve => {
            const wake = () => {
                clearTimeout(timer)
                onlineTarget.removeEventListener('online', wake)
                resolve()
            }
            const timer = setTimeout(wake, delayMs)
            onlineTarget.addEventListener('online', wake)
        })
    }
}
